import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Box } from '@mui/material';

const RippleEffect = ({ 
  children, 
  color = 'rgba(255, 255, 255, 0.3)',
  duration = 0.6,
  disabled = false,
  onClick,
  ...props 
}) => {
  const [ripples, setRipples] = useState([]);

  const addRipple = useCallback((event) => {
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;
    const newRipple = {
      x,
      y,
      size,
      id: Date.now() + Math.random()
    };

    setRipples(prev => [...prev, newRipple]);
    
    setTimeout(() => {
      setRipples(prev => prev.filter(ripple => ripple.id !== newRipple.id));
    }, duration * 1000);
  }, [duration]);

  const handleClick = useCallback((event) => {
    if (!disabled) {
      addRipple(event);
      if (onClick) {
        onClick(event);
      }
    }
  }, [addRipple, onClick, disabled]);

  const rippleVariants = {
    initial: {
      scale: 0,
      opacity: 0.8
    },
    animate: {
      scale: 2,
      opacity: 0,
      transition: {
        duration: duration,
        ease: "easeOut"
      }
    }
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        cursor: disabled ? 'default' : 'pointer',
        ...props.sx
      }}
      {...props}
    >
      {children}
      <AnimatePresence>
        {ripples.map((ripple) => (
          <motion.div
            key={ripple.id}
            variants={rippleVariants}
            initial="initial"
            animate="animate"
            exit={{ opacity: 0 }}
            style={{
              position: 'absolute',
              left: ripple.x,
              top: ripple.y,
              width: ripple.size,
              height: ripple.size,
              borderRadius: '50%',
              backgroundColor: color,
              pointerEvents: 'none',
              zIndex: 0
            }}
          />
        ))}
      </AnimatePresence>
    </Box>
  );
};

const PulseEffect = ({ 
  children, 
  color = 'rgba(2, 62, 138, 0.2)',
  scale = 1.05,
  disabled = false,
  trigger = 'hover',
  ...props 
}) => {
  const pulseVariants = {
    initial: { scale: 1, opacity: 0 },
    animate: {
      scale: scale,
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  const triggerProps = trigger === 'hover' 
    ? { whileHover: !disabled ? "animate" : "initial" }
    : { whileTap: !disabled ? "animate" : "initial" };

  return (
    <motion.div
      initial="initial"
      variants={pulseVariants}
      {...triggerProps}
      style={{
        position: 'relative',
        display: 'inline-block',
        ...props.style
      }}
      {...props}
    >
      <motion.div
        variants={pulseVariants}
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: 'inherit',
          backgroundColor: color,
          zIndex: -1
        }}
      />
      {children}
    </motion.div>
  );
};

const TouchFeedback = ({ 
  children, 
  scaleDown = 0.95,
  scaleUp = 1.02,
  disabled = false,
  onClick,
  ...props 
}) => {
  const feedbackVariants = {
    rest: { 
      scale: 1,
      filter: 'brightness(1)'
    },
    hover: { 
      scale: scaleUp,
      filter: 'brightness(1.1)',
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 25
      }
    },
    tap: { 
      scale: scaleDown,
      filter: 'brightness(0.9)',
      transition: {
        type: "spring",
        stiffness: 600,
        damping: 30
      }
    },
    disabled: {
      opacity: 0.6,
      scale: 1,
      filter: 'brightness(0.8)'
    }
  };

  return (
    <motion.div
      variants={feedbackVariants}
      initial="rest"
      whileHover={!disabled ? "hover" : "disabled"}
      whileTap={!disabled ? "tap" : "disabled"}
      animate={disabled ? "disabled" : "rest"}
      onClick={disabled ? undefined : onClick}
      style={{
        display: 'inline-block',
        cursor: disabled ? 'not-allowed' : 'pointer',
        ...props.style
      }}
      {...props}
    >
      {children}
    </motion.div>
  );
};

const FocusRing = ({ 
  children, 
  color = '#0066cc',
  width = 2,
  offset = 2,
  visible = false,
  ...props 
}) => {
  const ringVariants = {
    hidden: {
      scale: 0.9,
      opacity: 0
    },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 30
      }
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-block',
        ...props.sx
      }}
      {...props}
    >
      {children}
      <AnimatePresence>
        {visible && (
          <motion.div
            variants={ringVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            style={{
              position: 'absolute',
              inset: -offset,
              border: `${width}px solid ${color}`,
              borderRadius: 'inherit',
              pointerEvents: 'none',
              zIndex: 1
            }}
          />
        )}
      </AnimatePresence>
    </Box>
  );
};

const GlowEffect = ({ 
  children, 
  color = '#0066cc',
  intensity = 0.3,
  size = 10,
  disabled = false,
  ...props 
}) => {
  const glowVariants = {
    rest: {
      boxShadow: 'none'
    },
    glow: {
      boxShadow: `0 0 ${size}px ${color}${Math.floor(intensity * 255).toString(16).padStart(2, '0')}`,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.div
      variants={glowVariants}
      initial="rest"
      whileHover={!disabled ? "glow" : "rest"}
      style={{
        display: 'inline-block',
        borderRadius: 'inherit',
        ...props.style
      }}
      {...props}
    >
      {children}
    </motion.div>
  );
};

export { 
  RippleEffect, 
  PulseEffect, 
  TouchFeedback, 
  FocusRing, 
  GlowEffect 
};